import { CircularProgress } from "@mui/material";
import { GradientButton } from "../../GradientButton";
import { useEffect, useState } from "react";
import { useGetAccountsPlanListQuery } from "../../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { TargetedAccountPlansModal } from "../../modal";
import { CustomButton } from "../../CustomButton";
import { useStateWithSearchParams } from "../../../../hooks";
import { QUERY_SEPARATOR } from "../../../../constant";
import { IndicatorTimeframe } from "../../../../types";
import { AccountPlanStatus } from "../../../../types/enums";

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  page?: string;
  per_page?: string;
  selected?: string;
  limit?: string;
  date?: string;
  period?: string;
};

const PER_PAGE = 25;
const START_PAGE = 0;

export const TargetAccountPlansBtn = ({
  campaign_id,
  isActionBlock,
  isGeneratingAllAccountPlan,
  hideBtn,
  onClose,
  setIsLoading,
}: {
  campaign_id: string;
  isActionBlock?: boolean;
  isGeneratingAllAccountPlan?: boolean;
  hideBtn?: boolean;
  onClose?: () => void;
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(START_PAGE);
  const [search, setSearch] = useState<string>("");

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    industries: undefined,
    regions: undefined,
    sizes: undefined,
    search: undefined,
    page: undefined,
    per_page: undefined,
    selected: undefined,
    limit: undefined,
    date: undefined,
    period: undefined,
  });

  useEffect(() => {
    setSearch(searchDict.search || "");
  }, [searchDict.search]);

  const setSearchHandler = (value: string) => {
    setSearch(value);
    setPage(START_PAGE);
  };

  const { data, isFetching, isLoading, error } = useGetAccountsPlanListQuery(
    {
      campaign_id: campaign_id!,
      page,
      per_page: PER_PAGE,
      limit: searchDict.limit ? Number(searchDict?.limit) : undefined,
      size: searchDict.sizes?.split(QUERY_SEPARATOR),
      search: search || undefined,
      industry: searchDict.industries?.split(QUERY_SEPARATOR),
      region: searchDict.regions?.split(QUERY_SEPARATOR),
      // sort: searchDict.sort?.split("-")[0],
      // order: searchDict.sort?.split("-")[1] as Order,
      start_date: searchDict.date?.split(QUERY_SEPARATOR)[0],
      end_date: searchDict.date?.split(QUERY_SEPARATOR)[1],
      status_filter: AccountPlanStatus.ready,
      indicator_timeframe: searchDict.period
        ? (searchDict.period.split(" ")[1].toLowerCase() as IndicatorTimeframe)
        : undefined,
    },
    { skip: !isOpen },
  );

  useEffect(() => {
    setIsLoading && setIsLoading(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (error) {
      toast.error(
        <CustomToast
          type="error"
          title="Failed to get targeted account list."
          text={
            getErrorMessage(error).globalError ||
            "Something went wrong. Please try again"
          }
        />,
      );
    }
  }, [error]);

  const openHandler = () => setIsOpen(true);

  useEffect(() => {
    hideBtn && setIsOpen(true);
  }, [hideBtn]);

  const onCloseHandler = () => {
    setIsOpen(false);
    onClose && onClose();
  };

  return (
    <>
      {!hideBtn && (
        <>
          {isActionBlock ? (
            <GradientButton
              fullWidth={false}
              onClick={openHandler}
              startIcon={
                isLoading && (
                  <CircularProgress size={24} sx={{ color: "inherit" }} />
                )
              }
              disabled={isFetching}
            >
              Get Targeted Account Plan
            </GradientButton>
          ) : (
            <CustomButton
              onClick={openHandler}
              variant="outlined"
              customColor="#FFFFFF"
              textColor="#BD4D00"
              customSize="fit"
              type="button"
              startIcon={
                (isLoading || isGeneratingAllAccountPlan) && (
                  <CircularProgress size={24} sx={{ color: "inherit" }} />
                )
              }
              disabled={isLoading || isGeneratingAllAccountPlan}
            >
              Get Targeted Account Plan
            </CustomButton>
          )}
        </>
      )}

      {isOpen && data && (
        <TargetedAccountPlansModal
          page={page}
          loadMore={() => setPage((prev) => prev + 1)}
          isLoading={isFetching}
          canLoadMore={data.total_pages > page + 1}
          campaign_id={campaign_id}
          accounts={data?.items || []}
          isOpen={isOpen}
          onClose={onCloseHandler}
          setSearchHandler={setSearchHandler}
          startPage={START_PAGE}
        />
      )}
    </>
  );
};
